import type { TerminologyOverrides, TermConcept } from '../types/install'

/**
 * The platform's business vocabulary, as configuration.
 *
 * Administration must not hard-code business-facing words. A deployment that
 * calls housing "Lodging", properties "Listings" or students "Members" is not a
 * fork of the product — it is the same product with a different vocabulary.
 *
 * This register is deliberately SEMANTIC, not a find-and-replace dictionary.
 * A concept carries its own singular, plural and short forms, so nothing has to
 * guess at grammar by appending an "s" — "Property" does not pluralise that
 * way, and neither does "Bursary".
 *
 * Route identifiers are NOT part of this. Renaming a concept must never change
 * `/admin/housing`, because bookmarks, permissions, API references and
 * integrations are all keyed to the technical id.
 */
export interface TermDefinition {
  /** Stable technical id. Never renamed, never derived from the label. */
  id: TermConcept
  singular: string
  plural: string
  /** Where a narrow surface (collapsed sidebar, chip) needs a shorter form. */
  short: string
  /** What the concept means here, so a rename is made knowingly. */
  meaning: string
}

export const TERMINOLOGY: Record<TermConcept, TermDefinition> = {
  platform: {
    id: 'platform',
    singular: 'Campuzon',
    plural: 'Campuzon',
    short: 'Campuzon',
    meaning: 'The product itself, as people refer to it.',
  },
  student: {
    id: 'student',
    singular: 'Student',
    plural: 'Students',
    short: 'Student',
    meaning: 'A person using the platform for their own education and campus life.',
  },
  institution: {
    id: 'institution',
    singular: 'Institution',
    plural: 'Institutions',
    short: 'Institution',
    meaning: 'A university, polytechnic or college with its own campuses.',
  },
  campus: {
    id: 'campus',
    singular: 'Campus',
    plural: 'Campuses',
    short: 'Campus',
    meaning: 'A single physical site belonging to an institution.',
  },
  housing: {
    id: 'housing',
    singular: 'Housing',
    plural: 'Housing',
    short: 'Housing',
    meaning: 'The accommodation domain as a whole — search, applications, tenancy.',
  },
  property: {
    id: 'property',
    singular: 'Property',
    plural: 'Properties',
    short: 'Property',
    meaning: 'One building or residence a manager operates.',
  },
  listing: {
    id: 'listing',
    singular: 'Listing',
    plural: 'Listings',
    short: 'Listing',
    meaning: 'One advertised home within a property.',
  },
  roommate: {
    id: 'roommate',
    singular: 'Roommate',
    plural: 'Roommates',
    short: 'Roommate',
    meaning: 'Someone a student may share a home with.',
  },
  discover: {
    id: 'discover',
    singular: 'Discover',
    plural: 'Discover',
    short: 'Discover',
    meaning: 'University, programme and funding discovery before admission.',
  },
  homepage: {
    id: 'homepage',
    singular: 'Homepage',
    plural: 'Homepage',
    short: 'Home',
    meaning: 'The public front door of the platform.',
  },
  marketplace: {
    id: 'marketplace',
    singular: 'Marketplace',
    plural: 'Marketplace',
    short: 'Market',
    meaning: 'Campus commerce — knowledge, services and provider orders.',
  },
  provider: {
    id: 'provider',
    singular: 'Provider',
    plural: 'Providers',
    short: 'Provider',
    meaning: 'An organisation or person selling a service on the platform.',
  },
  employer: {
    id: 'employer',
    singular: 'Employer',
    plural: 'Employers',
    short: 'Employer',
    meaning: 'An organisation recruiting through the platform.',
  },
  opportunity: {
    id: 'opportunity',
    singular: 'Opportunity',
    plural: 'Opportunities',
    short: 'Role',
    meaning: 'A role, internship or gig a student can apply for.',
  },
  community: {
    id: 'community',
    singular: 'Community',
    plural: 'Communities',
    short: 'Community',
    meaning: 'A space students join around a course, club or interest.',
  },
  representative: {
    id: 'representative',
    singular: 'Campus representative',
    plural: 'Campus representatives',
    short: 'Rep',
    meaning: 'An authorised field operator working on a campus.',
  },
  service: {
    id: 'service',
    singular: 'Academic service',
    plural: 'Academic services',
    short: 'Service',
    meaning: 'A transcript, reference or other institutional request.',
  },
  administrator: {
    id: 'administrator',
    singular: 'Administrator',
    plural: 'Administrators',
    short: 'Admin',
    meaning: 'Internal staff operating this console.',
  },
}

export const TERM_CONCEPTS = Object.keys(TERMINOLOGY) as TermConcept[]

/** The default vocabulary, so "reset to default" is a real, exact operation. */
export function defaultTerminology(): TerminologyOverrides {
  return {}
}

/**
 * Resolves one form of one concept. An override is a *claim*: a blank or
 * whitespace-only value falls back to the default rather than rendering an
 * empty label, because a navigation item with no name is unusable.
 */
export function resolveTerm(
  overrides: TerminologyOverrides | undefined,
  concept: TermConcept,
  form: 'singular' | 'plural' | 'short' = 'singular',
): string {
  const definition = TERMINOLOGY[concept]
  const override = overrides?.[concept]?.[form]
  const value = typeof override === 'string' ? override.trim() : ''
  if (value) return value
  // A configured singular with no short form supplied should still be honoured
  // in the short slot, rather than reverting to the shipped word.
  if (form === 'short') {
    const singular = overrides?.[concept]?.singular?.trim()
    if (singular) return singular
  }
  return definition[form]
}
